// useLetterColor.ts
import * as React from 'react';

const PALETTE = ['#9F91FF', '#A4D89C', '#A4D89C', '#F5978E'];

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function normalizeChar(ch: string): string {
  return ch
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100;
  const ln = l / 100;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = ln - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

export function getMutedColors(hex: string): { bg: string; text: string } {
  const [h] = hexToHsl(hex);
  return {
    bg: hslToHex(h, 35, 28),
    text: hslToHex(h, 45, 70),
  };
}

const useLetterColor = (value: string | undefined | null): string =>
  React.useMemo(() => {
    if (!value) return PALETTE[0];

    const first = value.trim();
    if (!first) return PALETTE[0];

    const ch = normalizeChar(first[0]);
    const idx = ALPHABET.indexOf(ch);

    if (idx === -1) return PALETTE[0];

    const group = Math.min(
      PALETTE.length - 1,
      Math.floor((idx * PALETTE.length) / ALPHABET.length)
    );

    return PALETTE[group];
  }, [value]);

export default useLetterColor;
